import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { FunctionComponent, useEffect, useState, useMemo, useCallback } from 'react';
import { Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import eyeOffIcon from '../../../assets/images/icons/eye-off.svg';
import eyeIcon from '../../../assets/images/icons/eye.svg';
import LoaderButton from '../../../components/common/LoaderButton';
import Input from '../../../components/Input';
import Label from '../../../components/Label';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  clearAuthState,
  clearCognitoStorage,
  loginUserThunk,
  setCredentials,
} from '../api/authSlice';
import { LoginSchema } from '../schemas';
import maintenanceIcon from '../../../assets/alert-triangle.svg';
import { featureFlagsState } from '../api/featureFlagSlice';
import ConfigService from '../../../api/services/ConfigService';
import LanguageSelectionModal from './LanguageSelectionModal';
import alert from '../../../utils/alert';
import useMfaEnabledFlag from '../../../hooks/useIsMfaEnabledFlag';

type LoginFormData = {
  email: string;
  password: string;
};

type BannerConfig = {
  heading: string;
  text: string;
};

export const LoginForm: FunctionComponent = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, dirtyFields },
  } = useForm<LoginFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(LoginSchema),
  });

  const { t }: any = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const auth = useAppSelector((state) => state.auth);
  const { featureFlags } = useAppSelector(featureFlagsState);

  const [inputFieldType, setInputFieldType] = useState('password');
  const [bannerConfigValues, setBannerConfigValues] = useState<BannerConfig>();
  const [bannerConfigValuesMaintenance, setBannerConfigValuesMaintenance] =
    useState<BannerConfig>();
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showMagicLinkOption, setShowMagicLinkOption] = useState(false);

  const togglePasswordVisibility = () => {
    setInputFieldType(inputFieldType === 'text' ? 'password' : 'text');
  };

  useEffect(() => {
    clearCognitoStorage();
  }, []);

  const isMfaEnabled = useMfaEnabledFlag();

  const onSubmit = async (data: LoginFormData) => {
    const { email, password } = data;

    try {
      const res = await dispatch(loginUserThunk({ username: email, password })).unwrap();
      dispatch(setCredentials({ ...res, isLoading: false }));
    } catch (error: any) {
      let message = error.error || 'Something went wrong. Please try again.';
      if (error instanceof Error) message = error.message;
      if (message == 'Invalid username or password.') {
        setShowMagicLinkOption(true); // Show magic link button
      }
      alert.error(t(message), { toastId: 'login-error' });
      navigate('/auth/login');
    }
  };

  const handleAuthNavigation = useCallback(
    (res: any, email: string) => {
      if (res.newPasswordRequired) {
        navigate('/auth/reset-password?newPasswordRequired=true', {
          state: { email },
          replace: true,
        });
        return;
      }
      if (res.status === 'success') {
        if (isMfaEnabled) {
          navigate('/auth/mfa-preference', {
            state: { email },
          });
        } else {
          navigate('/');
        }
      }
    },
    [navigate, isMfaEnabled],
  );

  useEffect(() => {
    const identifier = auth.email || auth.username;
    if (identifier) {
      handleAuthNavigation(auth, identifier);
    }
  }, [auth, handleAuthNavigation]);

  useEffect(() => {
    dispatch(clearAuthState());
  }, [dispatch]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('@language');
    if (!savedLanguage) {
      setShowLanguageModal(true);
    }
  }, []);

  const isBannerEnabled = useMemo(() => {
    const featureFlag = featureFlags.find((flag) => flag.feature_name === 'Banner');
    return (
      featureFlags.length &&
      (!featureFlag?.is_active || (featureFlag?.is_active && featureFlag?.is_enabled))
    );
  }, [featureFlags]);

  useEffect(() => {
    ConfigService.getConfigValues('Mobile', 'banner')
      .then((resp) => {
        setBannerConfigValues(resp.resource?.[0].config_value);
      })
      .catch((error) => {
        console.error('Error fetching config values:', error);
      });
  }, [isBannerEnabled]);

  const isBannerEnabledMaintenance = useMemo(() => {
    const featureFlag = featureFlags.find((flag) => flag.feature_name === 'BannerLoginMessage');
    return (
      featureFlags.length &&
      (!featureFlag?.is_active || (featureFlag?.is_active && featureFlag?.is_enabled))
    );
  }, [featureFlags]);

  useEffect(() => {
    ConfigService.getConfigValues('Mobile', 'banner')
      .then((resp) => {
        setBannerConfigValuesMaintenance(resp.resource?.[0].config_value);
      })
      .catch((error) => {
        console.error('Error fetching config values:', error);
      });
  }, [isBannerEnabledMaintenance]);

  return (
    <div>
      <LanguageSelectionModal show={showLanguageModal} onHide={() => setShowLanguageModal(false)} />
      {isBannerEnabled ? (
        <div className='maintenance-mode'>
          {maintenanceIcon && (
            <img src={maintenanceIcon} alt='Maintenance' className='maintenance-image' />
          )}
          <h1 className='maintenance-title'>{bannerConfigValues?.heading}</h1>
          <p className='maintenance-description'>{bannerConfigValues?.text}</p>
        </div>
      ) : (
        <div className='login-wrapper d-flex flex-column justify-content-center'>
          {isBannerEnabledMaintenance === true && (
            <div className='maintenance-mode mb-4'>
              <p className='maintenance-description'>{bannerConfigValuesMaintenance?.text}</p>
            </div>
          )}
          <div className='d-flex flex-column align-items-start'>
            <h2 className='auth__page-title'>{t('Login')}</h2>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className={'pt-2'}>
            <Form.Group controlId='email' className='auth__input-group'>
              <Label>{t('Email')}</Label>
              <Input
                type='text'
                placeholder={t('Email')}
                className={clsx(
                  [errors.email && 'is-invalid'],
                  [
                    dirtyFields.email && !errors.email
                      ? 'is-valid auth__input-no-bootstrap-icon'
                      : '',
                  ],
                )}
                {...register('email')}
              />
              {errors.email && <div className='invalid-feedback'>{errors.email.message}</div>}
            </Form.Group>

            <Form.Group
              controlId='password'
              className='auth__input-group auth__input-group--relative'
            >
              <Label>{t('Password')}</Label>
              <Input
                placeholder={t('Password')}
                type={inputFieldType}
                className={clsx('auth__input-no-bootstrap-icon', [
                  dirtyFields.password && errors.password && 'is-invalid',
                ])}
                data-testid={'password-input'}
                {...register('password')}
              />
              {errors.password && <div className='invalid-feedback'>{errors.password.message}</div>}
              <span
                role={'button'}
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && togglePasswordVisibility()}
                className='auth__input-group-icon'
                onClick={togglePasswordVisibility}
              >
                {inputFieldType === 'password' ? (
                  <img src={eyeOffIcon} width={20} height={20} alt='eye-off-icon' />
                ) : (
                  <img src={eyeIcon} width={20} height={20} alt='eye-icon' />
                )}
              </span>
            </Form.Group>

            <div className='d-flex flex-column align-items-center'>
              {showMagicLinkOption && (
                <p className='auth__help-text'>
                  <Link to='/auth/login/magic-link' className='auth__link text-primary-700'>
                    {t('Log In using a Magic Link')}
                  </Link>
                </p>
              )}
              <p className='auth__help-text'>
                <Link className='auth__link text-primary-700' to={'/auth/forgot-password'}>
                  {t('Forgot Password?')}
                </Link>
              </p>
              <LoaderButton
                className={`auth__btn-primary`}
                type='submit'
                onClick={handleSubmit(onSubmit)}
                isSubmitting={auth.isLoading}
                disabled={!isValid}
              >
                {t('Login')}
              </LoaderButton>
            </div>
          </form>
          <div className='auth__find-me'>
            <p className='auth__find-me--text'>{t('Is this being provided by your employer?')}</p>
            <p className='auth__help-text'>
              <Link className='auth__link text-primary-700' to={'/auth/find-me'}>
                {t('Find Me')}
              </Link>
            </p>
          </div>
          <div className='already_have_account-container'>
            <Link
              to='/auth/register'
              className='auth__link text-primary-700'
              data-testid='login-createAccount-link'
            >
              {t(`Don't have an employer-funded account? Create an account here`)}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};
